import type { ApiCopyContentDto, ApiCopyLibraryContentDto } from '@mfw/common';
import type { Locale } from '@mfw/translations';

import type { TranslatableRow } from '../utils/locales';

import { getLabelFromTranslations, getTranslation } from '../utils';
import { fetchNutritionPlans } from './nutritionPlans.api';
import { fetchExercises } from './exercises.api';
import { fetchGroceries } from './groceries.api';
import { fetchWorkouts } from './workouts.api';
import { fetchPrograms } from './programs.api';
import { fetchEpisodes } from './episodes.api';
import { fetchCourses } from './courses.api';
import { fetchModules } from './modules.api';
import { fetchMeals } from './meals.api';
import { get, post } from './axios';

export interface ContentCopyReport {
  perType: {
    workout: number;
    program: number;
    exercise: number;
    meal: number;
    nutritionPlan: number;
    grocery: number;
    course: number;
    module: number;
    episode: number;
  };
}

export type CopyContentSelection = ApiCopyContentDto['selection'];

// WS2 — clones the selected own content (+ its authored dependencies) from
// the source org into another org the caller works in. Source org untouched.
export async function copyContentToOrganization({
  organizationId,
  ...dto
}: { organizationId: string } & ApiCopyContentDto) {
  const response = await post<ContentCopyReport>(
    `/v2/organizations/${organizationId}/content-copy`,
    dto,
  );
  return response?.data ?? null;
}

// Clones ONE acquired library item (and its whole graph) into an organization
// the caller works in. `organizationId` is the TARGET — the item belongs to no
// organization, which is the reason this call exists; the body names the
// LIBRARY ROW, which is what proves the caller acquired the content.
export async function copyLibraryContentToOrganization({
  organizationId,
  ...dto
}: { organizationId: string } & ApiCopyLibraryContentDto) {
  const response = await post<{ id: string }>(
    `/v2/organizations/${organizationId}/library-content-copy`,
    dto,
  );
  return response?.data ?? null;
}

/**
 * One organization that already holds a live copy of a Saved item — whoever
 * made it. `copiedByMe` only chooses the wording; it does not decide whether
 * the organization counts as taken.
 */
export interface LibraryOrganizationCopy {
  organizationId: string;
  copiedByMe: boolean;
  copiedAt: string;
}

// Where this Saved item already is. Read by the copy dialog before it offers a
// destination — an organization named here is exactly an organization
// `copyLibraryContentToOrganization` would refuse with a 409.
export async function fetchLibraryOrganizationCopies(libraryId: string) {
  const response = await get<{ data: LibraryOrganizationCopy[] }>(
    `/v2/my-library/${libraryId}/organization-copies`,
  );
  return response?.data ?? [];
}

export type CopyableContentType =
  | 'course'
  | 'episode'
  | 'exercise'
  | 'grocery'
  | 'meal'
  | 'module'
  | 'nutritionPlan'
  | 'program'
  | 'workout';

export interface CopyableContentItem {
  id: string;
  label: string;
}

export interface FetchCopyableContentArgs {
  type: CopyableContentType;
  organizationId: string;
  userId: string;
  locale: Locale;
}

// Ui education translations don't share the exact `UiTranslation` shape the
// library/nutrition mappers produce — try both label helpers.
function labelOf(
  locale: Locale,
  item: { translations?: TranslatableRow[]; name?: string; id: string },
) {
  return (
    getLabelFromTranslations(locale, item.translations) ||
    getTranslation(locale, item.translations as never) ||
    item.name ||
    item.id
  );
}

// WS2 — flat "my own content in this org" list for the copy dialog's
// checkbox picker. `authorIds: [userId]` is the load-bearing filter (every
// list endpoint maps it to `userId IN`), capped at the DTO's 500-id limit.
export async function fetchCopyableContent({
  locale,
  organizationId,
  type,
  userId,
}: FetchCopyableContentArgs): Promise<CopyableContentItem[]> {
  const contentFilters = { authorIds: [userId] };
  // QueryAllDto caps `limit` at 100 — the picker shows the first 100 per type
  const tableFilters = { limit: '100', offset: '1' };
  const args = { contentFilters, locale, organizationId, tableFilters };
  const toItems = (
    data: Array<{
      translations?: TranslatableRow[];
      name?: string;
      id?: string;
    }>,
  ) =>
    data.flatMap((item) =>
      item.id
        ? [{ id: item.id, label: labelOf(locale, { ...item, id: item.id }) }]
        : [],
    );

  switch (type) {
    case 'workout':
      return toItems((await fetchWorkouts(args))?.data ?? []);
    case 'program':
      return toItems((await fetchPrograms(args))?.data ?? []);
    case 'exercise':
      return toItems((await fetchExercises(args))?.data ?? []);
    case 'meal':
      return toItems((await fetchMeals(args))?.data ?? []);
    case 'nutritionPlan':
      return toItems((await fetchNutritionPlans(args))?.data ?? []);
    case 'grocery':
      return toItems((await fetchGroceries(args))?.data ?? []);
    case 'course':
      return toItems((await fetchCourses(args))?.data ?? []);
    case 'module':
      return toItems((await fetchModules(args))?.data ?? []);
    case 'episode':
      return toItems((await fetchEpisodes(args))?.data ?? []);
  }
}
